import { useEffect, useState } from "react";
import api from "@/lib/api";
import BookButton from "@/components/BookButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useSEO, breadcrumbLd } from "@/lib/seo";

export default function Accommodations() {
  const [rooms, setRooms] = useState([]);
  const [open, setOpen] = useState(null);

  useSEO({
    title: "Rooms & Suites — Jacuzzi Suite, Family Suite, King & Queen Rooms",
    description:
      "Six room types at Westbridge Inn & Suites in Carrollton, MO: King Suite, Jacuzzi Suite, Three-Bedroom Family Suite, Queen Double, Standard Queen and Accessible Queen. Kids under 18 stay free.",
    path: "/accommodations",
    jsonLd: breadcrumbLd([{ name: "Home", path: "/" }, { name: "Rooms", path: "/accommodations" }]),
  });

  useEffect(() => {
    api.get("/rooms").then((r) => setRooms(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="accommodations-page">
      <section className="pt-36 pb-16 bg-canvas">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <p className="font-mono-label text-ember mb-4">Accommodations</p>
            <h1 className="font-display text-6xl md:text-7xl text-ink leading-[0.98]">
              Thirty-four rooms.<br />
              <span className="italic text-ember">Six ways to stay.</span>
            </h1>
          </div>
          <div className="md:col-span-4">
            <p className="text-ink-soft leading-relaxed">
              From an in-room jacuzzi to a three-bedroom family suite — every room includes a
              refrigerator, microwave, coffee maker, free Wi-Fi and cable TV. Bath linens are crisp,
              beds are firm, rates are fair every day of the week, and kids under 18 stay free in
              existing beds.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-canvas pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 space-y-20 md:space-y-32">
          {rooms.map((room, idx) => (
            <article
              key={room.id}
              className={`grid md:grid-cols-12 gap-10 md:gap-16 items-center ${idx % 2 === 1 ? "md:[direction:rtl]" : ""}`}
              data-testid={`room-${room.slug}`}
            >
              <div className="md:col-span-7 md:[direction:ltr]">
                <div className="aspect-[16/11] overflow-hidden bg-dune">
                  <img src={room.image_url} alt={`${room.name} — Westbridge Inn & Suites`} className="w-full h-full object-cover" loading="lazy" />
                </div>
              </div>
              <div className="md:col-span-5 md:[direction:ltr]">
                <p className="font-mono-label text-ember mb-3">
                  Room {String(idx + 1).padStart(2, "0")} · {room.bed_config}
                </p>
                <h2 className="font-display text-5xl text-ink leading-[1.02]">{room.name}</h2>
                <p className="mt-6 text-ink-soft leading-relaxed text-lg">{room.description}</p>
                <dl className="mt-8 grid grid-cols-3 gap-6 border-t border-rule-subtle pt-6">
                  <div>
                    <dt className="font-mono-label text-ink-muted">Sleeps</dt>
                    <dd className="font-display text-2xl text-ink mt-1">{room.max_guests}</dd>
                  </div>
                  {room.size_sqft && (
                    <div>
                      <dt className="font-mono-label text-ink-muted">Size</dt>
                      <dd className="font-display text-2xl text-ink mt-1">{room.size_sqft} <span className="text-base text-ink-muted">sq ft</span></dd>
                    </div>
                  )}
                  {room.price_from && (
                    <div>
                      <dt className="font-mono-label text-ink-muted">From</dt>
                      <dd className="font-display text-2xl text-ember mt-1">${room.price_from}<span className="text-base text-ink-muted">/nt</span></dd>
                    </div>
                  )}
                </dl>
                <ul className="mt-8 flex flex-wrap gap-x-5 gap-y-2">
                  {room.features.map((f) => (
                    <li key={f} className="font-mono-label text-ink-soft before:content-['·'] before:mr-3 before:text-ember">
                      {f}
                    </li>
                  ))}
                </ul>
                <div className="mt-10 flex flex-wrap gap-3">
                  <BookButton label="Reserve this room" testid={`room-book-${room.slug}`} />
                  <button onClick={() => setOpen(room)} className="btn-ink" data-testid={`room-details-${room.slug}`}>
                    Room details
                  </button>
                </div>
                {room.ada_accessible && (
                <p className="mt-6 inline-flex items-center gap-2 font-mono-label text-ember">
                  Accessible room · Details on the Accessibility page
                </p>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Accessibility note — details live on /accessibility */}
      <section className="bg-sand py-14" data-testid="ada-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <p className="text-ink-soft leading-relaxed max-w-2xl">
            <span className="font-display text-ink text-xl block mb-1">Accessible stays</span>
            We offer accessible queen rooms with roll-in showers and visual alerts — the same clean,
            quiet Westbridge night. Details on our Accessibility page, or call us and we'll set it up.
          </p>
          <a href="/accessibility" className="btn-ink whitespace-nowrap" data-testid="ada-learn-more">
            Accessibility details
          </a>
        </div>
      </section>

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="max-w-3xl bg-canvas border-rule-strong rounded-none" data-testid="room-detail-modal">
          {open && (
            <>
              <DialogHeader>
                <p className="font-mono-label text-ember">{open.bed_config} · sleeps {open.max_guests}</p>
                <DialogTitle className="font-display text-4xl text-ink">{open.name}</DialogTitle>
                <DialogDescription className="text-ink-soft text-base leading-relaxed">{open.description}</DialogDescription>
              </DialogHeader>
              <div className="aspect-[16/9] overflow-hidden bg-dune mt-4">
                <img src={open.image_url} alt={open.name} className="w-full h-full object-cover" />
              </div>
              {open.gallery?.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {open.gallery.map((g) => (
                    <img key={g} src={g} alt="" className="aspect-square object-cover" />
                  ))}
                </div>
              )}
              <div className="mt-6">
                <p className="font-mono-label text-ink-muted mb-3">In this room</p>
                <ul className="flex flex-wrap gap-x-5 gap-y-2">
                  {open.features.map((f) => (
                    <li key={f} className="font-mono-label text-ink-soft before:content-['·'] before:mr-3 before:text-ember">
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-6">
                <BookButton label="Reserve this room" testid="modal-book-btn" />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
